import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/useToast';
import {
  getPurchaseSettings,
  savePurchaseSettings,
  resetPurchaseSettings,
  type PurchaseSettings,
  PAYMENT_TERMS_OPTIONS,
} from '@/lib/settings';
import {
  RotateCcw,
  Save,
  Info,
  ShoppingCart,
  Truck,
  ClipboardCheck,
} from 'lucide-react';

export default function SettingsPurchaseTab() {
  const [settings, setSettings] = useState<PurchaseSettings>(getPurchaseSettings);
  const [saved, setSaved] = useState(false);
  const { success: toastSuccess } = useToast();

  useEffect(() => {
    setSettings(getPurchaseSettings());
  }, []);

  const handleSave = () => {
    savePurchaseSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    toastSuccess('Settings saved', 'Purchase settings have been updated.');
  };

  const handleReset = () => {
    setSettings(resetPurchaseSettings());
    toastSuccess('Settings reset', 'Purchase settings have been reset to defaults.');
  };

  const updateSetting = <K extends keyof PurchaseSettings>(
    key: K,
    value: PurchaseSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Default Order Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Default Order Settings
          </CardTitle>
          <CardDescription>
            Configure default values for purchase orders
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="paymentTerms">Default Payment Terms</Label>
              <select
                id="paymentTerms"
                value={settings.defaultPaymentTerms}
                onChange={(e) => updateSetting('defaultPaymentTerms', e.target.value as PurchaseSettings['defaultPaymentTerms'])}
                className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary w-full"
              >
                {PAYMENT_TERMS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-text-muted">
                Default payment terms for new purchase orders
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deliveryDays">Default Delivery Days</Label>
              <Input
                id="deliveryDays"
                type="number"
                min={1}
                max={365}
                value={settings.defaultDeliveryDays}
                onChange={(e) => updateSetting('defaultDeliveryDays', Number(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-text-muted">
                Expected delivery time in days
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receiving Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Receiving Options
          </CardTitle>
          <CardDescription>
            Configure how goods are received
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="allowPartialReceiving" className="text-base">
                Allow Partial Receiving
              </Label>
              <p className="text-sm text-text-muted">
                Allow receiving orders in multiple shipments
              </p>
            </div>
            <Switch
              id="allowPartialReceiving"
              checked={settings.allowPartialReceiving}
              onCheckedChange={(checked) => updateSetting('allowPartialReceiving', checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="requireQC" className="text-base">
                Require Quality Control
              </Label>
              <p className="text-sm text-text-muted">
                Items must pass QC before being marked as received
              </p>
            </div>
            <Switch
              id="requireQC"
              checked={settings.requireQCBeforeReceiving}
              onCheckedChange={(checked) => updateSetting('requireQCBeforeReceiving', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Automation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Automation
          </CardTitle>
          <CardDescription>
            Automatic actions for purchase workflow
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="autoCreatePO" className="text-base">
                Auto-Create PO from Approved Requisitions
              </Label>
              <p className="text-sm text-text-muted">
                Automatically create purchase order when requisition is approved
              </p>
            </div>
            <Switch
              id="autoCreatePO"
              checked={settings.autoCreatePOFromApprovedReq}
              onCheckedChange={(checked) => updateSetting('autoCreatePOFromApprovedReq', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleReset}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </Button>
        <Button onClick={handleSave}>
          <Save className="mr-2 h-4 w-4" />
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      {/* Info */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-text-secondary">
              <p className="font-medium text-text-primary mb-1">Settings are stored locally</p>
              <p>
                These settings are saved in your browser and will persist across sessions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
